<template>
  <div :class="mapLayersOptions ? 'map-with-legend' : ''">
    <div class="map">
      <div v-if="vtrObject.limitIsReached" class="pa-2 tracking-records-warning">{{ $t('map.tracking_records.warning', { firstPos: firstPosTimestamp, lastPos: lastPosTimestamp }) }}</div>
      <l-map
        ref="vtr-map"
        :padding="padding"
        :options="{ preferCanvas: true }"
        @ready="onMapReady"
      >
        <l-tile-layer v-bind="tilesOpts" />
        <l-layer-group ref="vtr-journey-layer" />
        <l-layer-group ref="vtr-raw-data-layer" />
        <l-control position="topleft" class="leaflet-bar leaflet-control">
          <a @click="recenterMap">
            <v-icon class="leaflet-center">mdi-fit-to-page-outline</v-icon>
          </a>
        </l-control>
      </l-map>
    </div>
    <div
      v-if="!width"
      :class="width ? 'option-usual' : 'option-reduced'"
    >
      <TrackingRecordsMapOptions
        v-if="mapLayersOptions"
        :mapLayersOptions="mapLayersOptions"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { LControl, LLayerGroup, LMap, LTileLayer, LTooltip } from 'vue2-leaflet';
import moment from 'moment';
import L, { CircleMarker,
  LatLngTuple,
  PointExpression,
  Polyline,
  circleMarker,
  polyline } from 'leaflet';
import './styles/tracking-records-map.scss';
import { LastPosFeatureCollection } from 'ctp-obu-tracker';
import { colors } from '@/constants/theme/colors';
import { MAP_ATTRIBUTION, TILES_URL } from '@/constants/map/mapConstants';
import TrackingRecordsMapOptions from '@/components/common/TrackingRecordsMapOptions/TrackingRecordsMapOptions.vue';

interface VtrObject {
  geometry: {
    coordinates: LatLngTuple[];
    type: string;
  };
  properties: {
    confidence: number;
    linkId: string;
    start: string;
  };
  type: string;
}

@Component({
  components: {
    LMap,
    LTileLayer,
    LLayerGroup,
    LTooltip,
    LControl,
    TrackingRecordsMapOptions
  }
})
export default class TrackingRecordsMap extends Vue {
    private padding: PointExpression = [25, 25];

    private map: L.Map;

    private vtrJourneyLayer: L.LayerGroup;

    private vtrRawDataLayer: L.LayerGroup;

    private tilesOpts = {
      url: TILES_URL,
      attribution: MAP_ATTRIBUTION
    };

    private markerOpts = {
      radius: 4,
      fillOpacity: 1,
      weight: 1,
      color: colors.secondaryDarker,
      fillColor: colors.secondary
    };

    private layersOptions: {
      [key: string]: () => Promise<Polyline[] | CircleMarker[]>,
    } = {
      mapMatched: this.displayDeviceJourney,
      raw: this.displayTrackingRecord
    };

    @Prop({
      type: Boolean, default: false
    })
    private width!: boolean;

    @Prop({
      type: Array, default: () => []
    })
    private vtr!: LatLngTuple[];

    @Prop({
      type: Array, default: () => []
    })
    private vtrDeviceJourney!: VtrObject[];

    @Prop({
      type: Array
    })
    private layers!: Array<'raw' | 'mapMatched'>;

    @Prop({
      type: Array
    })
    private mapLayersOptions!: string[];

    @Prop({
      type: Object
    })
    private vtrObject!: LastPosFeatureCollection;

    @Watch('layers')
    private async onSelectedLayersChange() {
      this.refreshMap();
      await this.recenterMap();
    }

    private get lastPosTimestamp() {
      return this.vtrObject.lastPosTimestamp ? moment(this.vtrObject.lastPosTimestamp * 1000).format('DD/MM/YYYY HH:mm:ss') : '';
    }

    private get firstPosTimestamp() {
      return this.vtrObject.firstPosTimestamp ? moment(this.vtrObject.firstPosTimestamp * 1000).format('DD/MM/YYYY HH:mm:ss') : '';
    }

    private async onMapReady() {
      const mapComponent = this.$refs['vtr-map'] as LMap;
      const vtrJourneyLayer = this.$refs['vtr-journey-layer'] as LLayerGroup;
      const vtrRawDataLayer = this.$refs['vtr-raw-data-layer'] as LLayerGroup;
      this.map = mapComponent.mapObject;
      this.vtrJourneyLayer = vtrJourneyLayer.mapObject;
      this.vtrRawDataLayer = vtrRawDataLayer.mapObject;
      await this.displayMapLayers(this.layers);
      await this.recenterMap();
    }

    private normalizeVtrPoints = async (points: LatLngTuple[]): Promise<LatLngTuple[]> => points.map(e => [e[1], e[0]]);

    private normalizeDeviceJourneyTracks = (points: VtrObject[]): LatLngTuple[][] => points.map(e => e.geometry.coordinates.map(x => [x[1], x[0]]));

    private normalizeDeviceJourneyPoints = (points: VtrObject): LatLngTuple[] => points.geometry.coordinates.map(x => [x[1], x[0]]);

    private refreshMap() {
      if (this.map) {
        this.vtrJourneyLayer.clearLayers();
        this.vtrRawDataLayer.clearLayers();
        this.displayMapLayers(this.layers);
      }
    }

    private async displayMapLayers(layers: Array<'raw' | 'mapMatched'>):
     Promise<Array<Polyline[] | CircleMarker[]> | CircleMarker[]> {
      if (layers) {
        return Promise
          .all(Object.keys(this.layersOptions)
            .filter((x: 'raw' | 'mapMatched' | any) => layers.includes(x))
            .map(async (elem: string | any | ('raw' | 'mapMatched')):
          Promise<Polyline[] | CircleMarker[]> => this.layersOptions[elem]()));
      }
      return this.displayTrackingRecord();
    }

    private async displayTrackingRecord(): Promise<CircleMarker[]> {
      const points = await this.normalizeVtrPoints(this.vtr);
      const pointsData = this.vtrObject.features.map((x: any) => {
        const { coordinates } = x.geometry;

        return {
          coordinates,
          ...x.properties
        };
      });

      return Promise.all(points.map((latLng: LatLngTuple, i: number) => {
        let marker = circleMarker(latLng, this.markerOpts);
        const data = pointsData[i];

        const tooltipDOMString = `
        <div>
          <div>
            <span class="tooltip-important-data">
              ${!i ? 'Start' : ''}
              ${i === pointsData.length - 1 ? 'End' : ''}
            </span>
          </div>
          <div class="${i === 0 || i === pointsData.length - 1 ? 'mt-1' : 'mb-0'}">
            <span class="tooltip-header">Time: </span>
          </div>
          <div id="tooltip-value mb-1">
            ${moment.unix(data.timestamp).format('DD/MM/YYYY HH:mm:ss')}
          </div>
          <div class="mt-1">
            <span class="tooltip-header">Position: </span>
          </div>
          <div id="tooltip-value mb-1">
            Lat: ${data.coordinates[1]}°,
            Lon: ${data.coordinates[0]}°
          </div>
          <div class="mt-1">
            <span
              class="tooltip-header"
              style="display: ${data.hdop === undefined ? 'none' : 'inherit'}">
                HDOP:
            </span>
          </div>
          <div
            id="tooltip-value mb-1"
            style="display: ${data.hdop === undefined ? 'none' : 'inherit'}"
          >
            ${data.hdop}
          </div>
          <div class="mt-1">
            <span
              class="tooltip-header"
              style="display: ${data.cellID === undefined ? 'none' : 'inherit'}"
            >
              CellID:
            </span>
          </div>
          <div
            id="tooltip-value mb-1"
            style="display: ${data.cellID === undefined ? 'none' : 'inherit'}"
          >
            ${data.cellID}
          </div>
          <div class="mt-1">
            <span
              class="tooltip-header"
              style="display: ${data.elevation === undefined ? 'none' : 'inherit'}"
            >
              Elevation:
            </span>
          </div>
          <div
            id="tooltip-value mb-1"
            style="display: ${data.elevation === undefined ? 'none' : 'inherit'}"
          >
            ${data.elevation} meter${Math.abs(data.elevation) > 1 ? 's' : ''}
          </div>
          <div class="mt-1">
            <span
              class="tooltip-header"
              style="display: ${data.heading === undefined ? 'none' : 'inherit'}"
            >
              Heading:
            </span>
          </div>
          <div
            id="tooltip-value mb-1"
            style="display: ${data.heading === undefined ? 'none' : 'inherit'}"
          >
            ${data.heading}°
          </div>
          <div class="mt-1">
            <span
              class="tooltip-header"
              style="display: ${data.speed === undefined ? 'none' : 'inherit'}"
            >
              Speed:
            </span>
          </div>
          <div
            id="tooltip-value mb-1"
            style="display: ${data.speed === undefined ? 'none' : 'inherit'}"
          >
            ${data.speed} km/h
          </div>
          <div class="mt-1">
            <span
              class="tooltip-header"
              style="display: ${data.obuStatus === undefined ? 'none' : 'inherit'}"
            >
              OBE Status:
            </span>
          </div>
          <div
            id="tooltip-value mb-1"
            style="display: ${data.obuStatus === undefined ? 'none' : 'inherit'}"
          >
            ${data.obeStatus}
          </div>
          <div class="mt-1">
            <span
              class="tooltip-header"
              style="display: ${data.exemption === undefined ? 'none' : 'inherit'}"
            >
              OBE Status:
            </span>
          </div>
          <div
            id="tooltip-value mb-1"
            style="display: ${data.exemption === undefined ? 'none' : 'inherit'}"
          >
            ${data.exemption}
          </div>
          <div class="mt-1">
            <span
              class="tooltip-header"
              style="display: ${data.privateMode === undefined ? 'none' : 'inherit'}"
            >
              OBE Status:
            </span>
          </div>
          <div
            id="tooltip-value mb-1"
            style="display: ${data.privateMode === undefined ? 'none' : 'inherit'}"
          >
            ${data.privateMode}
          </div>
          <div class="mt-1">
            <span
              class="tooltip-header"
              style="display: ${data.tollingState === undefined ? 'none' : 'inherit'}"
            >
              OBE Status:
            </span>
          </div>
          <div
            id="tooltip-value mb-1"
            style="display: ${data.tollingState === undefined ? 'none' : 'inherit'}"
          >
            ${data.tollingState}
          </div>
          <div class="mt-1">
            <span
              class="tooltip-header"
              style="display: ${data.counter === undefined ? 'none' : 'inherit'}"
            >
              Position Counter:
            </span>
          </div>
          <div
            id="tooltip-value mb-1"
            style="display: ${data.counter === undefined ? 'none' : 'inherit'}"
          >
            ${data.counter}
          </div>
          <div class="mt-1">
            <span
              class="tooltip-header"
              style="display: ${data.lastCounter === undefined ? 'none' : 'inherit'}"
            >
              Last position counter:
            </span>
          </div>
          <div
            id="tooltip-value mb-1"
            style="display: ${data.lastCounter === undefined ? 'none' : 'inherit'}"
          >
            ${data.lastCounter}
          </div>
        </div>
      `;

        marker.bindPopup(tooltipDOMString);
        if (i === 0) {
          marker = marker.bindPopup(tooltipDOMString)
            .setRadius(this.markerOpts.radius * 2)
            .setStyle({
              color: colors.startTrip.outline,
              fillColor: colors.startTrip.fill,
              weight: 2
            });
        }
        if (i === points.length - 1) {
          marker = marker.bindPopup(tooltipDOMString)
            .setRadius(this.markerOpts.radius * 2)
            .setStyle({
              color: colors.endTrip.outline,
              fillColor: colors.endTrip.fill,
              weight: 2
            });
        }
        return marker.addTo(this.vtrRawDataLayer);
      }));
    }

    private async displayDeviceJourney(): Promise<Polyline[]> {
      return Promise.all(this.vtrDeviceJourney
        .map((vtrTrack: VtrObject) => {
          const normalizedCoordinate = this.normalizeDeviceJourneyPoints(vtrTrack);
          const polylineChunk = polyline(normalizedCoordinate, {
            weight: 7,
            color: '#48b144'
          });
          return polylineChunk.addTo(this.vtrJourneyLayer);
        }));
    }

    private async recenterMap() {
      try {
        const vtrJourneyCollection: any = this.vtrJourneyLayer.toGeoJSON();
        if (vtrJourneyCollection && vtrJourneyCollection.features) {
          let coordinates: LatLngTuple[] = this
            .normalizeDeviceJourneyTracks(vtrJourneyCollection.features).flat();
          if (!coordinates.length) {
            const computeCoordinates = async (): Promise<LatLngTuple[]> => this
              .normalizeVtrPoints(this.vtr);
            coordinates = await computeCoordinates();
          }
          this.map.fitBounds(coordinates, {
            padding: this.padding
          });
        }
      } catch {}
    }
}
</script>

<style lang="scss" scoped>
@import './styles/tracking-records-map.scss';
</style>
